import { Component, Input, OnInit } from '@angular/core';
import { first, forkJoin, mergeMap } from 'rxjs';
import { Availability } from '../../../../models/availability';
import { CustomerProductPrice } from '../../../../models/price';
import { Product } from '../../../../models/product';
import { AvailabilityService } from '../../../../services/availability/availability.service';
import { CartService } from '../../../../services/cart/cart.service';
import { PriceService } from '../../../../services/price/price.service';
import { ProductService } from '../../../../services/products/product.service';
import { SelectedCustomerService } from '../../../../services/selected-customer/selected-customer.service';
import { ProductSuggestionData } from '../../services/chat-stream-event.model';

/** One suggested product hydrated with the live data its card needs. */
interface SuggestedProductCard {
  product: Product;
  price?: CustomerProductPrice;
  availability?: Availability;
  // The LLM's suggested amount for this product (>= 1, defaults to 1). Shown as a badge and used
  // by "add all to cart".
  suggestedQty: number;
}

@Component({
  selector: 'gung-glippy-product-suggestion',
  standalone: false,
  templateUrl: './glippy-product-suggestion.component.html',
  styleUrl: './glippy-product-suggestion.component.scss'
})
export class GlippyProductSuggestionComponent implements OnInit {
  @Input() data!: ProductSuggestionData;
  cards: SuggestedProductCard[] = [];
  expanded = false;

  constructor(
    private productService: ProductService,
    private priceService: PriceService,
    private availabilityService: AvailabilityService,
    private selectedCustomerService: SelectedCustomerService,
    private cartService: CartService
  ) {}

  ngOnInit(): void {
    const ids = this.data?.productIds ?? [];
    if (ids.length === 0) {
      return;
    }
    forkJoin([
      this.productService.getProductsByIds(ids).pipe(first()),
      this.selectedCustomerService.getSelectedCustomer().pipe(
        first(),
        mergeMap(customer => this.priceService.getCustomerPrices(ids, customer.id).pipe(first()))
      ),
      this.availabilityService.getAvailabilities(ids).pipe(first())
    ]).subscribe(([products, prices, availabilities]) => {
      // A hydration miss (product removed since the suggestion was emitted) drops that
      // card, never errors the chat (ADR 0004). Cards keep the suggestion's order.
      this.cards = ids
        .map(id => (products ?? []).find(product => product?.id === id))
        .filter((product): product is Product => !!product)
        .map(product => ({
          product,
          price: (prices ?? []).find(price => price.productId === product.id),
          availability: (availabilities ?? []).find(av => av.productId === product.id),
          suggestedQty: this.data.suggestedQuantities?.[product.id] ?? 1
        }));
    });
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  /**
   * Adds every hydrated card to the cart at its suggested amount in one dispatch (additive — on top
   * of what's already in the cart). A card dropped by a hydration miss contributes nothing.
   */
  addAllToCart(): void {
    if (this.cards.length === 0) {
      return;
    }
    this.cartService.bulkAddToCart(
      this.cards.map(card => ({ productId: card.product.id, qty: card.suggestedQty }))
    );
  }
}
